import { ETL_API } from '../../../config/etlApi';
import { getDefaultAiConfig, hasConfiguredAi } from '../../../config/aiConfig';
import { previewMessagesForNode, getUpstreamGatewayMessages } from './pipelineUpstream';

const INPUT_NODE_TYPES = new Set(['kGateway', 'mqttInput', 'httpInput']);

const SENSITIVE_CONFIG_KEYS = new Set([
  'aiApiKey',
  'password',
  'apiKey',
  'secret',
  'token',
  'connectionString',
]);

/** All ETL node types that support AI assist. */
export const AI_ASSIST_NODE_TYPES = new Set([
  'kGateway',
  'mqttInput',
  'httpInput',
  'filter',
  'switch',
  'throttle',
  'dedupe',
  'join',
  'aggregate',
  'window',
  'jsonParser',
  'mapper',
  'function',
  'bleDecoder',
  'aiTransform',
  'bleAutoDetect',
  'bleElaPtEn',
  'bleKbPro',
  'bleSigmaWit',
  'viewNode',
  'mqttOutput',
  'redisSink',
  'supabaseSink',
  'mongoSink',
  'postgresSink',
  'mysqlSink',
  'natsSink',
  'qstashPublish',
  'qstashSchedule',
  'webhook',
]);

export const NODE_ASSIST_LABELS = {
  kGateway: 'gateway topic',
  mqttInput: 'MQTT subscription',
  httpInput: 'HTTP input',
  filter: 'filter condition',
  switch: 'routing rules',
  throttle: 'rate limit',
  dedupe: 'dedupe key',
  join: 'join settings',
  aggregate: 'aggregation',
  window: 'time window',
  jsonParser: 'JSON path',
  mapper: 'field mappings',
  function: 'transform code',
  bleDecoder: 'byte mappings',
  aiTransform: 'AI transform',
  bleAutoDetect: 'BLE detection',
  bleElaPtEn: 'ELA sensor',
  bleKbPro: 'KBPro sensor',
  bleSigmaWit: 'SigmaWit sensor',
  viewNode: 'data view',
  mqttOutput: 'MQTT publish',
  redisSink: 'Redis sink',
  supabaseSink: 'Supabase table',
  mongoSink: 'MongoDB collection',
  postgresSink: 'PostgreSQL table',
  mysqlSink: 'MySQL table',
  natsSink: 'NATS subject',
  qstashPublish: 'QStash publish',
  qstashSchedule: 'QStash schedule',
  webhook: 'webhook URL',
};

function stripCodeFences(text = '') {
  return String(text)
    .replace(/^```[\w]*\n?/gm, '')
    .replace(/```$/gm, '')
    .trim();
}

/** Sanitize config before sending to AI (no secrets). */
export function sanitizeConfigForAi(config = {}) {
  const out = {};
  Object.entries(config).forEach(([key, value]) => {
    if (SENSITIVE_CONFIG_KEYS.has(key)) return;
    if (value == null || value === '') return;
    out[key] = value;
  });
  return out;
}

/** Latest payload for AI — upstream preview, or live MQTT for input nodes. */
export function extractSamplePayload(nodeId, nodeType, config, nodes, edges, liveMessages) {
  if (INPUT_NODE_TYPES.has(nodeType)) {
    const { messages, topic } = getUpstreamGatewayMessages(nodeId, nodes, edges, liveMessages);
    const msg = messages[0] || liveMessages[0];
    if (!msg) return null;
    return {
      payload: msg.payload ?? msg.data?.payload ?? msg,
      topic: msg.topic || topic || config.mqtt_topic || config.topic || '',
    };
  }

  const { previewMessages, messages } = previewMessagesForNode(
    nodeId,
    nodeType,
    config,
    nodes,
    edges,
    liveMessages,
  );
  const msg = previewMessages[0] || messages[0];
  if (!msg) return null;
  return {
    payload: msg.payload ?? msg.data?.payload ?? msg,
    topic: msg.topic || '',
  };
}

export function canAssistForNode(config = {}, liveMessages = [], sample, { requireSample = false } = {}) {
  const ai = { ...getDefaultAiConfig(), ...config };
  if (!hasConfiguredAi(ai)) return false;
  if (!requireSample) return true;
  if (sample?.payload != null) return true;
  return Array.isArray(liveMessages) && liveMessages.length > 0;
}

export async function fetchNodeAssist({
  targetNode,
  samplePayload,
  topic = '',
  config = {},
  userHint = '',
  explainOnly = false,
}) {
  const ai = { ...getDefaultAiConfig(), ...config };
  if (!hasConfiguredAi(ai)) {
    throw new Error('AI provider not configured');
  }

  const response = await fetch(`${ETL_API}/ai/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider: ai.aiProvider,
      api_key: ai.aiApiKey || '',
      mode: 'node_assist',
      target_node: targetNode,
      prompt: userHint || (explainOnly ? 'Explain this node and the sample data only.' : undefined),
      current_config: sanitizeConfigForAi(config),
      sample_messages: samplePayload != null
        ? [{ topic, payload: samplePayload }]
        : [],
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || response.statusText || 'AI assist failed');
  }

  const result = await response.json();
  return {
    explanation: result.explanation || stripCodeFences(result.generated || ''),
    suggestions: result.suggestions || {},
    raw: result.generated,
  };
}

/** Backward-compatible single-field suggest (legacy node_suggest mode). */
export async function fetchNodeSuggestion({
  targetNode,
  samplePayload,
  config = {},
  userHint = '',
  topic = '',
}) {
  const assist = await fetchNodeAssist({
    targetNode,
    samplePayload,
    topic,
    config,
    userHint,
  });
  return assist.explanation || JSON.stringify(assist.suggestions);
}

function coerceBool(value) {
  return value === true || value === 'true';
}

function coerceNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
}

/** Apply structured AI suggestions to node config fields. */
export function applyNodeAssistSuggestions(suggestions, handleChange, targetNode) {
  if (!suggestions || typeof suggestions !== 'object') return;

  Object.entries(suggestions).forEach(([key, value]) => {
    if (value == null || value === '') return;

    if (key === 'operations') {
      const ops = Array.isArray(value)
        ? value
        : String(value).split(',').map((s) => s.trim()).filter(Boolean);
      handleChange('operations', ops);
      return;
    }

    if (key === 'qos' || key === 'rate' || key === 'windowSize' || key === 'duration'
      || key === 'interval' || key === 'joinWindow' || key === 'ttl') {
      handleChange(key, coerceNumber(value));
      return;
    }

    if (key === 'retain' || key === 'useTLS' || key === 'emitOnEach') {
      handleChange(key, coerceBool(value));
      return;
    }

    if (key === 'cases' && Array.isArray(value)) {
      handleChange('cases', value.join('\n'));
      return;
    }

    handleChange(key, value);
  });

  // Legacy switch: property + cases on separate lines in one field
  if (targetNode === 'switch' && suggestions.property && !suggestions.cases) {
    // property already applied
  }
}

/** Apply AI text output to the correct node config fields (legacy). */
export function applyNodeSuggestion(targetNode, generated, handleChange) {
  const text = stripCodeFences(generated);
  if (!text) return;

  try {
    const parsed = JSON.parse(text);
    if (parsed.suggestions) {
      applyNodeAssistSuggestions(parsed.suggestions, handleChange, targetNode);
      return;
    }
  } catch {
    // plain text fallback
  }

  switch (targetNode) {
    case 'filter':
      handleChange('condition', text.split('\n')[0].trim());
      break;
    case 'jsonParser':
      handleChange('jsonPath', text.split('\n')[0].trim());
      break;
    case 'mapper':
      handleChange('mappings', text.trim());
      break;
    case 'dedupe':
      handleChange('dedupeKey', text.split('\n')[0].trim());
      break;
    case 'switch': {
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (!lines.length) break;
      handleChange('property', lines[0]);
      if (lines.length > 1) {
        handleChange('cases', lines.slice(1).join('\n'));
      }
      break;
    }
    default:
      break;
  }
}

export const NODE_SUGGEST_LABELS = NODE_ASSIST_LABELS;
